import type { ChartConfig } from '@/shared/components/ui/chart'
import type { DemandSegmentModel } from '@/shared/generated'

import { useTranslation } from 'react-i18next'
import { Bar, BarChart, Cell } from 'recharts'

import { ChartContainer } from '@/shared/components/ui/chart'
import { cn } from '@/shared/lib/utils'

import { patternLabel } from '../lib/labels'

// Derived from the contract, so a new wire pattern fails the build here
// instead of silently shipping a quadrant with a missing cell.
type Pattern = DemandSegmentModel['pattern']

// bar silhouettes sketching each pattern (values 0..1; 0 = zero-sale week)
const CELL_PATTERNS: Record<Pattern, number[]> = {
	intermittent: [0.8, 0, 0.7, 0, 0.75, 0, 0.8],
	lumpy: [0.3, 0, 1, 0, 0.5, 0, 0.25],
	smooth: [0.7, 0.82, 0.74, 0.86, 0.78, 0.8, 0.76],
	erratic: [0.3, 0.9, 0.5, 1, 0.4, 0.72, 0.55],
}

// grid order: top row = high timing variability
const GRID: Pattern[] = ['intermittent', 'lumpy', 'smooth', 'erratic']

const sparkConfig = {} satisfies ChartConfig

// decorative tremor-style spark bars sketching the pattern silhouette
const CellSparkChart = ({
	pattern,
	active,
}: {
	pattern: Pattern
	active: boolean
}) => {
	const rows = CELL_PATTERNS[pattern].map((value, index) => ({
		slot: `w${index}`,
		value,
	}))

	return (
		<ChartContainer
			config={sparkConfig}
			className='mx-auto aspect-auto h-6 w-full max-w-24'
			aria-hidden
		>
			<BarChart
				accessibilityLayer={false}
				data={rows}
				margin={{ top: 1, right: 0, bottom: 0, left: 0 }}
				barCategoryGap='25%'
			>
				<Bar
					dataKey='value'
					maxBarSize={8}
					minPointSize={1.5}
					radius={[0.5, 0.5, 0, 0]}
					isAnimationActive={false}
				>
					{rows.map((row) => (
						<Cell
							key={row.slot}
							fill={
								row.value === 0 || !active
									? 'var(--muted-foreground)'
									: 'var(--primary)'
							}
							fillOpacity={row.value === 0 || !active ? 0.4 : 1}
						/>
					))}
				</Bar>
			</BarChart>
		</ChartContainer>
	)
}

export const DemandQuadrant = ({
	active,
	className,
}: {
	active: Pattern
	className?: string
}) => {
	const { t } = useTranslation()

	return (
		<figure className={className} data-testid='mtf-demand-quadrant'>
			<div className='grid grid-cols-2 gap-px overflow-hidden rounded-md border bg-border'>
				{GRID.map((pattern, index) => (
					<div
						key={pattern}
						data-testid={`mtf-quadrant-cell-${pattern}`}
						data-active={pattern === active}
						className={cn(
							'flex flex-col gap-1 bg-muted px-1.5 py-2',

							pattern === active &&
								'bg-primary/10 ring-1 ring-primary ring-inset',
							{
								'rounded-bl-md': index === 2,
								'rounded-tl-md': index === 0,
								'rounded-br-md': index === 3,
								'rounded-tr-md': index === 1,
							}
						)}
					>
						<span
							className={cn(
								'text-xs font-bold tracking-wider uppercase',
								pattern === active ? 'text-primary' : 'text-muted-foreground'
							)}
						>
							{patternLabel(pattern, t)}
						</span>
						<CellSparkChart pattern={pattern} active={pattern === active} />
					</div>
				))}
			</div>
			<figcaption className='mt-1 flex justify-between text-xs text-muted-foreground'>
				<span>
					{t('midterm_forecast.quadrant_x', {
						defaultValue: 'Quantity variability (CV²) →',
					})}
				</span>
				<span>
					{t('midterm_forecast.quadrant_y', {
						defaultValue: '↑ Timing variability (ADI)',
					})}
				</span>
			</figcaption>
		</figure>
	)
}
